import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import { ReturnsPopover } from './ReturnsPopover';
import { MappedReturn } from 'store/returns/models/returnData';

const { Text } = Typography;

export interface ScopePieChartData {
  label: string;
  value: number;
  color: string;
  onClick?: () => void;
  returns?: MappedReturn[];
}

export interface ScopePieChartProps {
  data: ScopePieChartData[];
  size?: number;
}

export const ScopePieChart: React.FC<ScopePieChartProps> = ({ data, size = 200 }) => {
  const { t, i18n } = useTranslation();
  const filteredData = data.filter((item) => item.value > 0);
  const total = filteredData.reduce((sum, item) => sum + item.value, 0);
  
  if (total === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Text style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>{t('global.no-data-available')}</Text>
      </div>
    );
  }

  const center = size / 2;
  const radius = size / 2 - 10;
  let currentAngle = -90; // Start from top

  const createPath = (startAngle: number, endAngle: number, largeArc: number) => {
    const start = {
      x: center + radius * Math.cos((startAngle * Math.PI) / 180),
      y: center + radius * Math.sin((startAngle * Math.PI) / 180),
    };
    const end = {
      x: center + radius * Math.cos((endAngle * Math.PI) / 180),
      y: center + radius * Math.sin((endAngle * Math.PI) / 180),
    };
    return `M ${center} ${center} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
  };

  // Pre-calculate all slice data including angles for overlays
  const slicesData = filteredData.map((item) => {
    const percentage = (item.value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    const midAngle = (startAngle + endAngle) / 2;
    const largeArc = angle > 180 ? 1 : 0;
    const path = createPath(startAngle, endAngle, largeArc);
    
    // Calculate overlay position at center of slice
    const overlayX = center + (radius * 0.6) * Math.cos((midAngle * Math.PI) / 180);
    const overlayY = center + (radius * 0.6) * Math.sin((midAngle * Math.PI) / 180);
    
    currentAngle = endAngle;
    
    return {
      ...item,
      startAngle,
      endAngle,
      midAngle,
      path,
      overlayX,
      overlayY,
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <svg width={size} height={size} style={{ cursor: 'pointer', display: 'block' }}>
          {slicesData.map((slice, index) => (
            <path
              key={index}
              d={slice.path}
              fill={slice.color}
              stroke="var(--color-surface-raised, #ffffff)"
              strokeWidth="2"
              onClick={slice.onClick}
              style={{ 
                cursor: slice.onClick ? 'pointer' : 'default', 
                pointerEvents: slice.returns && slice.returns.length > 0 ? 'none' : 'all' 
              }}
            />
          ))}
        </svg>
        {slicesData.map((slice, index) => {
          if (!slice.returns || slice.returns.length === 0) return null;
          
          return (
            <ReturnsPopover
              key={`popover-${index}`}
              returns={slice.returns}
              onClick={slice.onClick}
            >
              <div
                style={{
                  position: 'absolute',
                  left: slice.overlayX - radius * 0.5,
                  top: slice.overlayY - radius * 0.5,
                  width: radius,
                  height: radius,
                  cursor: 'pointer',
                  pointerEvents: 'all',
                  zIndex: 10,
                }}
                onClick={slice.onClick}
              />
            </ReturnsPopover>
          );
        })}
      </div>
      <div style={{ marginTop: '16px', width: '100%' }}>
        {filteredData.map((item, index) => {
          const rowContent = (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                cursor: item.onClick ? 'pointer' : 'default',
              }}
              onClick={item.onClick}
            >
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  borderRadius: '2px',
                  marginRight: '8px',
                }}
              />
              <Text style={{ fontSize: '14px', color: 'var(--color-text-dark, #042a2a)', flex: 1 }}>
                {item.label}
              </Text>
              <Text style={{ fontSize: '14px', color: 'var(--color-text-dark, #042a2a)', fontWeight: 600 }}>
                {formatDisplayInteger(item.value, i18n.language)}
              </Text>
            </div>
          );

          return item.returns && item.returns.length > 0 ? (
            <ReturnsPopover
              key={index}
              returns={item.returns}
              onClick={item.onClick}
            >
              {rowContent}
            </ReturnsPopover>
          ) : (
            <div key={index}>{rowContent}</div>
          );
        })}
      </div>
    </div>
  );
};

